import AdminOnly from '@/components/ui/AdminOnly';
import { droppedFolder } from '@/lib/dropped';
import { STEPS, STEP_COLOR, STEP_LABEL, STAGE_LABEL } from '@/lib/steps';
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Image as ImageIcon, Video, Layout, Type, Camera, BookOpen, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import {
  qk, fetchCreatives, fetchClients, fetchStrategies, createCreative,
  type Creative, type CreativeCreateBody,
} from '@/lib/queries';
import { uploadAsset } from '@/lib/upload';
import CreativeFormDialog from '@/components/creative/CreativeFormDialog';
import ViewToggle, { useViewMode } from '@/components/ui/ViewToggle';
import BulkUploadDialog from '@/components/creative/BulkUploadDialog';
import { useCan } from '@/hooks/use-scopes';
import { Upload } from 'lucide-react';
import { cn } from '@/lib/cn';



const TYPES = ['video', 'reel', 'static', 'carousel', 'story', 'photo', 'copy'] as const;
const TYPE_ICONS: Record<string, React.ReactNode> = {
  video: <Video size={12} />, reel: <Video size={12} />,
  static: <ImageIcon size={12} />, carousel: <Layout size={12} />, story: <BookOpen size={12} />,
  photo: <Camera size={12} />, copy: <Type size={12} />,
};

//: the months a person is likely to want, newest first. Built once, at module level,
//: so the list does not shift while the page is open.
const MONTHS = (() => {
  const out = [{ value: '', label: 'Any month' }];
  const now = new Date();
  for (let i = 0; i < 14; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({ value, label: d.toLocaleDateString('en-AE', { month: 'long', year: 'numeric' }) });
  }
  return out;
})();
export default function CreativesListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [bulk, setBulk] = useState(false);
  // Executors (own-tasks-only) never brief or bulk-upload across creatives.
  const canWrite = useCan('creatives.write');
  const [view, setView] = useViewMode('creatives', 'cards');
  // Filters live in the address, not in component state, so leaving for a creative
  // and pressing back brings them back with you. They used to reset to empty, which
  // silently widened the page to every clinic.
  const [params, setParams] = useSearchParams();
  const param = (k: string) => params.get(k) ?? '';
  const setParam = (k: string, v: string) => setParams((cur) => {
    const next = new URLSearchParams(cur);
    if (v) next.set(k, v); else next.delete(k);
    return next;
  }, { replace: true });
  const q = param('q');
  const setQ = (v: string) => setParam('q', v);
  // The server does the searching, over every creative rather than the loaded page,
  // so the typing is held back briefly instead of firing a query per keystroke.
  const [qSent, setQSent] = useState('');
  useEffect(() => { const t = setTimeout(() => setQSent(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  const clientId = param('client');
  const setClientId = (v: string) => setParam('client', v);
  const strategyId = param('strategy');
  const setStrategyId = (v: string) => setParam('strategy', v);
  const step = param('step');
  const setStep = (v: string) => setParam('step', v);
  //: which month's work to show. The shoot decides the month, the strategy stands in
  //: when there is no shoot. This is what replaces the strategy picker in daily use.
  const month = param('month');
  const setMonth = (v: string) => setParam('month', v);
  //: 'waiting' keeps the old meaning of the step chips: work sitting at that step.
  //: 'has' reaches any piece carrying that artefact, finished ones included.
  const chipMode = param('mode') === 'has' ? 'has' : 'waiting';
  const setChipMode = (v: string) => setParam('mode', v === 'has' ? 'has' : '');
  const order = param('order') || 'recent';
  const setOrder = (v: string) => setParam('order', v === 'recent' ? '' : v);
  const type = param('type');
  const setType = (v: string) => setParam('type', v);
  const funnel = param('funnel');
  const setFunnel = (v: string) => setParam('funnel', v);

  const clients = useQuery({ queryKey: qk.clients, queryFn: fetchClients });
  const strategies = useQuery({ queryKey: qk.strategies, queryFn: fetchStrategies });

  const create = useMutation({
    // Two-step: POST creative, then upload each chosen reference file as a kind=reference asset.
    mutationFn: async ({ body, refs }: { body: CreativeCreateBody; refs: File[] }) => {
      const c = await createCreative(body);
      if (refs.length) {
        for (const f of refs) {
          try { await uploadAsset({ creativeId: c.id, file: f, kind: 'reference', category: droppedFolder(f) ?? undefined }); }
          catch (e) { /* surface but don't abort the rest */ console.error('ref upload failed', f.name, e); }
        }
      }
      return c;
    },
    onSuccess: (c) => {
      toast.success(c.status === 'in_progress' ? `Submitted ${c.code}` : `Saved draft ${c.code}`);
      qc.invalidateQueries({ queryKey: qk.creatives });
      qc.invalidateQueries({ queryKey: qk.strategy(c.strategy_id) });
      setCreating(false);
      navigate(`/creatives/${c.id}`);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Create failed';
      toast.error(msg);
    },
  });

  const filterParams = {
    client_id: clientId, strategy_id: strategyId, creative_type: type, funnel_stage: funnel, q: qSent,
    month, order,
    // the same chips mean two different questions depending on the mode
    ...(chipMode === 'has' ? { has: step } : { step }),
  };
  const creatives = useQuery({
    queryKey: qk.creativesFiltered(filterParams),
    queryFn: () => fetchCreatives(filterParams),
  });

  // Already filtered by the server; kept as the list the page renders.
  const filtered = creatives.data ?? [];

  return (
    <div>
      <PageHeader
        title="Creatives"
        subtitle="Every video, reel, design, photo and copy across your clients."
        actions={
          <div className="flex items-center gap-2">
            <ViewToggle mode={view} onChange={setView} />
            {canWrite && (
              <Button variant="secondary" icon={<Upload size={14} />} onClick={() => setBulk(true)} title="Drop a whole shoot day; files route by the code in their name">
                Bulk upload
              </Button>
            )}
            {canWrite && (
              <Button icon={<Plus size={14} />} onClick={() => setCreating(true)}>
                New creative
              </Button>
            )}
          </div>
        }
      />
      {bulk && (
        <BulkUploadDialog
          creatives={(creatives.data ?? []).map((c) => ({ id: c.id, code: c.code, title: c.title }))}
          onClose={() => setBulk(false)}
          onDone={() => { setBulk(false); qc.invalidateQueries({ queryKey: ['creatives'] }); }}
        />
      )}

      <Card className="space-y-3 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <Input label="Search" placeholder="Title, code, doctor, treatment..." value={q} onChange={(e) => setQ(e.target.value)} />
          <Select
            label="Client"
            value={clientId}
            onChange={setClientId}
            searchable
            options={[
              { value: '', label: 'All clients' },
              ...(clients.data ?? []).map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <Select
            label="Strategy"
            value={strategyId}
            onChange={setStrategyId}
            searchable
            options={[
              { value: '', label: 'All strategies' },
              ...(strategies.data ?? []).map((s) => ({ value: s.id, label: s.title, hint: s.code })),
            ]}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-800">
          <div className="inline-flex rounded-full border border-slate-800 overflow-hidden" data-testid="chip-mode">
            {([['waiting', 'Waiting here'], ['has', 'Been through here']] as const).map(([v, label]) => (
              <button key={v} onClick={() => setChipMode(v)}
                className={cn('text-[11px] px-3 py-1.5 min-h-[32px] transition',
                  chipMode === v ? 'bg-accent-600/25 text-accent-400' : 'text-muted hover:text-muted-strong')}>{label}</button>
            ))}
          </div>
          <div className="w-36">
            <Select size="sm" value={month} onChange={setMonth} options={MONTHS} />
          </div>
          <div className="w-40">
            <Select size="sm" value={order} onChange={setOrder} options={[
              { value: 'recent', label: 'Most recent first' },
              { value: 'shoot', label: 'By shoot date' },
              { value: 'code', label: 'By code' },
            ]} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Pill label={`Step: ${step ? STEP_LABEL[step] ?? step : 'All'}`} active={!!step} onClear={() => setStep('')} />
          {STEPS.map((st) => (
            <button key={st.key} onClick={() => setStep(step === st.key ? '' : st.key)}
              className={cn('text-[11px] px-3 py-1.5 min-h-[32px] rounded-full border transition',
                step === st.key ? 'border-accent-400 bg-accent-600/20 text-accent-400'
                                : 'border-slate-800 text-muted hover:text-muted-strong')}>
              {st.label}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-muted -mt-1">
          {chipMode === 'has'
            ? 'Every piece that carries this, finished ones included. This is how you reach a script or a cut from weeks ago.'
            : 'Work sitting at this step right now.'}
        </p>
        <div className="flex flex-wrap gap-2">
          <Pill label={`Type: ${type || 'All'}`} active={!!type} onClear={() => setType('')} />
          {TYPES.map((t) => (
            <button key={t} onClick={() => setType(type === t ? '' : t)}
              className={cn('text-[11px] px-3 py-1.5 min-h-[32px] rounded-full border transition capitalize inline-flex items-center gap-1',
                type === t ? 'border-accent-400 bg-accent-600/20 text-accent-400'
                           : 'border-slate-800 text-muted hover:text-muted-strong')}>
              {TYPE_ICONS[t]} {t}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Pill label={`Funnel: ${funnel || 'All'}`} active={!!funnel} onClear={() => setFunnel('')} />
          {(['TOF', 'MOF', 'BOF'] as const).map((f) => (
            <button key={f} onClick={() => setFunnel(funnel === f ? '' : f)}
              className={cn('text-[11px] px-3 py-1.5 min-h-[32px] rounded-full border transition',
                funnel === f ? 'border-accent-400 bg-accent-600/20 text-accent-400'
                             : 'border-slate-800 text-muted hover:text-muted-strong')}>
              {f}
            </button>
          ))}
        </div>
      </Card>

      {creatives.isLoading && <div className="text-muted text-sm">Loading...</div>}

      {!creatives.isLoading && filtered.length === 0 && (
        <Card>
          <div className="text-center py-10 space-y-3">
            <ImageIcon className="mx-auto text-muted/60" size={36} />
            <p className="text-muted text-sm">No creatives match the current filters.</p>
            {canWrite && (
              <Button icon={<Plus size={14} />} onClick={() => setCreating(true)}>
                New creative
              </Button>
            )}
            <p className="text-[11px] text-muted/70">
              Or open a <Link to="/strategies" className="underline hover:text-accent-400">strategy</Link> to brief one of its planned slots.
            </p>
          </div>
        </Card>
      )}

      {view === 'cards' && (
        <div className="space-y-2">
          {filtered.map((c) => <CreativeRow key={c.id} c={c} />)}
        </div>
      )}

      {view === 'table' && filtered.length > 0 && (
        <Card>
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted border-b border-line">
                  <th className="font-medium py-2 pr-3 whitespace-nowrap">Code</th>
                  <th className="font-medium py-2 pr-3">Title</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap hidden md:table-cell">Client</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap">Stage</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap hidden md:table-cell">Type</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap hidden lg:table-cell">Funnel</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap hidden xl:table-cell">Posting</th>
                  <th className="font-medium py-2 whitespace-nowrap text-right hidden lg:table-cell">Revision</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/creatives/${c.id}`)}
                    className="cursor-pointer border-b border-line last:border-0 hover:bg-bg-soft transition"
                  >
                    <td className="py-2.5 pr-3 font-mono text-xs text-accent-400 whitespace-nowrap">{c.code}</td>
                    <td className="py-2.5 pr-3 text-muted-strong truncate max-w-[280px]">{c.title}</td>
                    <td className="py-2.5 pr-3 text-xs text-muted whitespace-nowrap hidden md:table-cell">{c.client_name}</td>
                    <td className="py-2.5 pr-3 whitespace-nowrap">
                      <span className={cn('px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide border', STEP_COLOR[c.step_key ?? ''] ?? '')}>
                        {c.step_label ?? STAGE_LABEL[c.current_stage]}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-xs text-muted-strong capitalize whitespace-nowrap hidden md:table-cell">{c.creative_type}</td>
                    <td className="py-2.5 pr-3 text-xs text-muted whitespace-nowrap hidden lg:table-cell">{c.funnel_stage}</td>
                    <td className="py-2.5 pr-3 text-xs text-muted whitespace-nowrap hidden xl:table-cell">{c.posting_date || '-'}</td>
                    <td className="py-2.5 text-right text-xs text-muted whitespace-nowrap hidden lg:table-cell"><AdminOnly inline>r{c.revision_round}</AdminOnly></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {creating && (
        <CreativeFormDialog
          mode="adhoc"
          defaultStrategyId={strategyId || undefined}
          onClose={() => setCreating(false)}
          onSubmit={(body, refs) => create.mutate({ body, refs })}
          submitting={create.isPending}
        />
      )}
    </div>
  );
}

function CreativeRow({ c }: { c: Creative }) {
  return (
    <Link to={`/creatives/${c.id}`}>
      <Card className="hover:border-accent-400/50 transition cursor-pointer">
        {/* Chips wrap and never squeeze: a pill that is allowed to shrink turns
            into a circle of three lines on a phone, and the date column then
            sits on top of it. Dates go under the text on narrow screens. */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1 text-xs" data-testid="creative-chips">
              <span className="font-mono text-accent-400 whitespace-nowrap">{c.code}</span>
              <span className={cn('px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide border whitespace-nowrap shrink-0', STEP_COLOR[c.step_key ?? ''] ?? '')}>
                {c.step_label ?? STAGE_LABEL[c.current_stage]}
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] bg-slate-700/40 text-slate-300 inline-flex items-center gap-1 capitalize whitespace-nowrap shrink-0">
                {TYPE_ICONS[c.creative_type]} {c.creative_type}
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] bg-bg text-muted border border-slate-800 whitespace-nowrap shrink-0">{c.funnel_stage}</span>
              {c.is_paid && <span className="px-2 py-0.5 rounded-full text-[10px] bg-orange-500/15 text-orange-300 whitespace-nowrap shrink-0">Paid</span>}
              {c.client_visible && <span className="px-2 py-0.5 rounded-full text-[10px] bg-emerald-500/15 text-emerald-300 whitespace-nowrap shrink-0">Client visible</span>}
            </div>
            <div className="text-sm text-muted-strong truncate">{c.title}</div>
            <div className="text-xs text-muted truncate">
              {c.client_name}
              {c.taxonomy_primary && ` · ${c.taxonomy_primary}`}
              {c.taxonomy_secondary && ` / ${c.taxonomy_secondary}`}
              {c.talent_name && ` · ${c.talent_name}`}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:block sm:text-right text-[11px] text-muted shrink-0" data-testid="creative-dates">
            {c.posting_date && <div className="whitespace-nowrap">Post {c.posting_date}</div>}
            {c.execution_date && <div className="whitespace-nowrap">Shoot {c.execution_date}</div>}
            <AdminOnly inline>r{c.revision_round}</AdminOnly>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function Pill({ label, active, onClear }: { label: string; active: boolean; onClear: () => void }) {
  if (!active) return <span className="text-[10px] uppercase tracking-wide text-muted self-center mr-1">{label}</span>;
  return (
    <button onClick={onClear} className="text-[10px] uppercase tracking-wide text-accent-400 self-center mr-1 underline underline-offset-2">
      {label} (clear)
    </button>
  );
}
